import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge, Metric, EmptyState } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeftRight, Receipt, Wallet, LogOut, Plus, Ban, ArrowRight, Clock, Hotel, FileText } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { useCurrency } from "@/lib/currency";
import { DocumentsPanel } from "@/components/documents/DocumentsPanel";
import { StayDocumentsList } from "@/components/documents/StayDocumentsList";
import { CollectPaymentDialog } from "@/components/documents/CollectPaymentDialog";
import { LateCheckoutBanner } from "@/components/pms/LateCheckoutBanner";
import { GuestInfoStrip } from "@/components/pms/GuestInfoStrip";
import { CheckoutFlow } from "@/components/checkout/CheckoutFlow";
import RoomChangeModal from "@/components/pms/RoomChangeModal";
import { docsAPI } from "@/lib/documents";
import { CategorySelect } from "@/components/documents/CategorySplit";
import { useCheckinGate, GateWrap } from "@/hooks/useCheckinGate";
import { EarlyCheckinPrompt } from "@/components/pms/EarlyCheckinPrompt";
import { MergeBillsButton } from "@/components/documents/MergeBillsDialog";

const SOURCE_MODULES = ["rooms", "restaurant", "bar", "costume_rental", "costume_sale", "laundry", "extra_bed", "late_checkout", "transport", "misc"];

export default function StayDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const money = useCurrency();
  const [stay, setStay] = useState(null);
  const [folio, setFolio] = useState(null);
  const [entries, setEntries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [rooms, setRooms] = useState([]);

  const [chargeOpen, setChargeOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [extOpen, setExtOpen] = useState(false);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const [chargeForm, setChargeForm] = useState({ source_module: "misc", description: "", quantity: 1, unit_price: 0, tax_rate: "" });
  const [folioSummary, setFolioSummary] = useState(null);
  const [extForm, setExtForm] = useState({ new_departure: "", reason: "" });
  const [checkinForm, setCheckinForm] = useState({ room_id: "", note: "" });
  const [earlyPrompt, setEarlyPrompt] = useState(null); // EARLY-CHECKIN
  const [tab, setTab] = useState("folio");
  const [docsKey, setDocsKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const s = await api.get(`/pms/stays/${id}`);
      setStay(s.data);
      // Rooms feed the room-change picker only; don't let a perms gap (e.g. a
      // Front Desk officer without master read) block folio/details loading.
      try {
        const r = await api.get("/master/rooms/?page_size=300");
        setRooms(r.data.items);
      } catch { setRooms([]); }
      if (s.data.folio_id) {
        const [f, es, ps] = await Promise.all([
          api.get(`/pms/folios/${s.data.folio_id}`),
          api.get(`/pms/folios/${s.data.folio_id}/entries`),
          api.get(`/pms/payments/?folio_id=${s.data.folio_id}`),
        ]);
        setFolio(f.data); setEntries(es.data.items); setPayments(ps.data.items);
        docsAPI.folioSummary(id).then(setFolioSummary).catch(() => setFolioSummary(null));
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const gate = useCheckinGate();

  if (!stay) return <div className="text-hocp-textMuted text-sm">Loading…</div>;

  // Prefer the master rooms list, but fall back to the last un-released
  // room_history entry on the stay itself so snapshot-only rooms (which
  // may not appear in the master `rooms` list) still show a number
  // instead of "—".
  const room = (() => {
    const fromMaster = rooms.find(r => r.id === stay.current_room_id);
    if (fromMaster) return fromMaster;
    const hist = (stay.room_history || []).slice().reverse()
      .find(h => h.room_id === stay.current_room_id && !h.released_at);
    return hist ? { id: hist.room_id, room_number: hist.room_number } : null;
  })();
  const balance = folio?.balance ?? 0;

  const gated = gate.isBlocked(stay?.expected_arrival);
  const lineByRef = Object.fromEntries((folioSummary?.lines || []).map((l) => [l.ref, l]));
  const doCheckin = async () => {
    if (gated) { toast.error(gate.reason(stay.expected_arrival)); return; }
    try {
      const { data: updated } = await api.post(`/pms/stays/${id}/checkin`, checkinForm);
      toast.success("Checked in"); setCheckinOpen(false); load();
      const rm = rooms.find((r) => r.id === checkinForm.room_id);
      setEarlyPrompt({ stay_id: id, folio_id: updated?.folio_id || stay?.folio_id, stay_code: stay?.code, room_number: rm?.room_number, guest_name: stay?.guest_name });
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doExt = async () => {
    try { await api.post(`/pms/stays/${id}/extend`, extForm); toast.success("Stay extended"); setExtOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const postCharge = async () => {
    try {
      const payload = { ...chargeForm, quantity: +chargeForm.quantity, unit_price: +chargeForm.unit_price };
      if (payload.tax_rate === "" || payload.tax_rate === null) delete payload.tax_rate;
      else payload.tax_rate = +payload.tax_rate;
      await api.post(`/pms/folios/${folio.id}/charge`, payload);
      toast.success("Charge posted"); setChargeOpen(false); setChargeForm({ source_module: "misc", description: "", quantity: 1, unit_price: 0, tax_rate: "" }); load();
    } catch (e) {
      if (e?.response?.status === 403) toast.error("You don't have the \"Post Charge\" permission. Ask an admin to enable it in Workforce › Permission Matrix › Finance › Folios & Billing.", { duration: 8000 });
      else toast.error(formatApiError(e));
    }
  };
  const voidEntry = async (eid) => {
    const reason = window.prompt("Void reason?");
    if (!reason) return;
    try { await api.post(`/pms/folios/entries/${eid}/void`, { reason }); toast.success("Voided"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const isCheckedIn = stay.status === "checked_in";

  return (
    <div data-testid="stay-detail-page">
      <PageHeader
        title={stay.guest_details?.name || stay.guest_name || "Guest"}
        subtitle={`Stay · ${stay.code}`}
        action={
          <div className="flex gap-2">
            {stay.group?.size > 1 && <Badge tone="info" data-testid="stay-group-chip">Room {stay.group.index} of {stay.group.size}</Badge>}
            <Badge tone={isCheckedIn ? "success" : stay.status === "reserved" ? "warning" : "neutral"}>{stay.status.toUpperCase().replace("_", " ")}</Badge>
            <Button variant="outline" onClick={() => nav("/pms/stays")} className="h-9 bg-transparent border-hocp-border hover:bg-hocp-bgSubtle">Back</Button>
          </div>
        }
      />

      <div className="mb-4">
        <GuestInfoStrip name={stay.guest_details?.name || stay.guest_name} phone={stay.guest_details?.phone} email={stay.guest_details?.email} testid="stay-guest-strip" />
      </div>
      {stay.group?.size > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-[12.5px] text-hocp-textSecondary" data-testid="stay-group-siblings">
          <span className="font-semibold text-hocp-textPrimary">Group booking · {stay.group.size} rooms</span>
          {stay.group.siblings.map((sib) => (
            <button key={sib.stay_id} type="button" onClick={() => nav(`/pms/stays/${sib.stay_id}`)} data-testid={`stay-group-sibling-${sib.index}`}
              className="rounded-full border border-hocp-border bg-hocp-bgSubtle px-3 py-1 hover:border-hocp-deepTeal hover:text-hocp-deepTeal transition-colors">
              Room {sib.index} · {sib.room_number || "—"} · <span className="mono">{sib.code}</span>{sib.status !== "checked_in" ? ` · ${sib.status.replace("_", " ")}` : ""}
            </button>
          ))}
          <MergeBillsButton stay={stay} className="ml-auto" testId="stay-merge-bills-btn" />
        </div>
      )}

      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Card className="p-4"><div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted mb-1">Current Room</div><div className="font-mono text-xl text-white">{room ? room.room_number : "—"}</div></Card>
        <Card className="p-4"><div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted mb-1">Arrival</div><div className="text-[13px] mono text-hocp-textPrimary">{stay.actual_arrival ? new Date(stay.actual_arrival).toLocaleDateString() : stay.expected_arrival}</div></Card>
        {stay.reservation_source && <Card className="p-4" data-testid="stay-source-card"><div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted mb-1">Source</div><div className="text-[13px] text-hocp-textPrimary font-medium truncate">{stay.reservation_source}</div>{stay.reservation_source_ref && <div className="text-[11px] mono text-hocp-textMuted truncate">Ref {stay.reservation_source_ref}</div>}</Card>}
        <Card className="p-4"><div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted mb-1">Departure</div><div className="text-[13px] mono text-hocp-textPrimary">{stay.expected_departure}</div></Card>
        <Card className="p-4"><div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted mb-1">Charges</div><div className="font-mono text-xl text-white">{money(folio?.total_charges ?? 0)}</div></Card>
        <Card className={`p-4 ${balance > 0 ? "border-amber-500/30" : "border-emerald-500/30"}`}>
          <div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted mb-1">Balance</div>
          <div className={`font-mono text-xl ${balance > 0 ? "text-amber-400" : "text-emerald-400"}`}>{money(balance)}</div>
        </Card>
      </div>

      {/* Action bar */}
      <Card className="p-3 mb-4 flex flex-wrap gap-2" data-testid="stay-action-bar">
        {stay.status === "reserved" && (
          <GateWrap blocked={gated} reason={gate.reason(stay.expected_arrival)} testid="checkin-gate">
            <Button onClick={() => setCheckinOpen(true)} disabled={gated} className="bg-emerald-500 hover:bg-emerald-600 text-white h-9 text-[12px]" data-testid="checkin-button"><Hotel className="w-3.5 h-3.5 mr-1.5"/>Check in</Button>
          </GateWrap>
        )}
        {isCheckedIn && <Button onClick={() => setChargeOpen(true)} variant="outline" className="h-9 text-[12px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="post-charge-button"><Plus className="w-3.5 h-3.5 mr-1.5"/>Post charge</Button>}
        {isCheckedIn && <Button onClick={() => setPayOpen(true)} variant="outline" className="h-9 text-[12px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="take-payment-button"><Wallet className="w-3.5 h-3.5 mr-1.5"/>Collect payment</Button>}
        {isCheckedIn && <Button onClick={() => setChangeOpen(true)} variant="outline" className="h-9 text-[12px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="change-room-button"><ArrowLeftRight className="w-3.5 h-3.5 mr-1.5"/>Change room</Button>}
        {isCheckedIn && <Button onClick={() => setExtOpen(true)} variant="outline" className="h-9 text-[12px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="extend-stay-button"><Clock className="w-3.5 h-3.5 mr-1.5"/>Extend</Button>}
        {isCheckedIn && <Button onClick={() => { setTab("documents"); setDocsKey((k) => k + 1); }} variant="outline" className="h-9 text-[12px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="provisional-bill-button"><FileText className="w-3.5 h-3.5 mr-1.5"/>Bills & Receipts</Button>}
        {isCheckedIn && <Button onClick={() => setCheckoutOpen(true)} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 text-[12px] ml-auto" data-testid="checkout-button"><LogOut className="w-3.5 h-3.5 mr-1.5"/>Checkout</Button>}
      </Card>

      <LateCheckoutBanner stayId={id} active={isCheckedIn} onApplied={() => { load(); setDocsKey((k) => k + 1); }} />

      <Tabs value={tab} onValueChange={setTab} className="space-y-3">
        <TabsList className="bg-hocp-bgSubtle border border-hocp-border">
          <TabsTrigger value="folio" data-testid="tab-folio">Folio Ledger ({entries.length})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments ({payments.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Bills & Receipts</TabsTrigger>
          <TabsTrigger value="rooms" data-testid="tab-rooms">Room History ({stay.room_history?.length || 0})</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-stay-timeline">Timeline</TabsTrigger>
        </TabsList>
        <TabsContent value="folio">
          {entries.length === 0 ? <EmptyState icon={Receipt} title="Folio is empty" subtitle="Post charges, discounts, or adjustments from the action bar."/> :
            <div className="border border-hocp-border rounded-md bg-hocp-bgCard overflow-hidden" data-testid="folio-entries-table">
              <table className="w-full">
                <thead><tr className="border-b border-hocp-border bg-hocp-bgSubtle">{["Time", "Type", "Module", "Description", "Category", "Qty", "Unit", "Tax", "Total", ""].map(h => <th key={h} className={`px-3 py-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-hocp-textMuted mono ${["Qty", "Unit", "Tax", "Total"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
                <tbody>
                  {entries.map(e => (
                    <tr key={e.id} className={`border-b border-hocp-border last:border-0 ${e.is_voided ? "opacity-40" : ""}`} data-testid={`folio-entry-${e.id}`}>
                      <td className="px-3 py-2.5 text-[10px] mono text-hocp-textTertiary">{new Date(e.posted_at).toLocaleString()}</td>
                      <td className="px-3 py-2.5"><Badge tone={e.entry_type === "charge" ? "info" : e.entry_type === "discount" ? "success" : e.entry_type === "adjustment" ? "warning" : "neutral"}>{e.entry_type}</Badge></td>
                      <td className="px-3 py-2.5 mono text-[11px] text-hocp-textMuted">{e.source_module}</td>
                      <td className="px-3 py-2.5 text-[12px] text-hocp-textPrimary">{e.description}{e.status === "pending_approval" && <Badge tone="warning">PENDING APPROVAL</Badge>}{e.is_voided && <Badge tone="error">VOID</Badge>}</td>
                      <td className="px-3 py-2.5">{e.entry_type === "charge" && !e.is_voided && lineByRef[e.id] && <CategorySelect stayId={id} line={lineByRef[e.id]} disabled={stay.status === "checked_out"} onChanged={() => docsAPI.folioSummary(id).then(setFolioSummary).catch(() => {})} />}</td>
                      <td className="px-3 py-2.5 text-right mono text-[11px] text-hocp-textMuted">{e.quantity}</td>
                      <td className="px-3 py-2.5 text-right mono text-[11px] text-hocp-textMuted">{money(e.unit_price)}</td>
                      <td className="px-3 py-2.5 text-right mono text-[11px] text-hocp-textMuted">{e.tax_amount ? money(e.tax_amount) : "—"}</td>
                      <td className="px-3 py-2.5 text-right mono text-[12px] text-white">{e.entry_type === "discount" ? `-${money(e.total)}` : money(e.total)}</td>
                      <td className="px-3 py-2.5 text-right">{!e.is_voided && e.status === "posted" && <Button size="sm" variant="ghost" onClick={() => voidEntry(e.id)} className="h-6 px-2 text-[10px] text-red-400" data-testid={`void-${e.id}`}><Ban className="w-3 h-3 mr-1"/>Void</Button>}</td>
                    </tr>
                  ))}
                  <tr className="bg-hocp-bgSubtle font-bold">
                    <td colSpan={8} className="px-3 py-2.5 text-right mono text-[10px] uppercase tracking-wider text-hocp-textMuted">Balance Due</td>
                    <td className={`px-3 py-2.5 text-right mono text-[14px] ${balance > 0 ? "text-amber-400" : "text-emerald-400"}`}>{money(balance)}</td>
                    <td/>
                  </tr>
                </tbody>
              </table>
            </div>
          }
        </TabsContent>
        <TabsContent value="payments">
          {payments.length === 0 ? <EmptyState icon={Wallet} title="No payments yet"/> :
            <div className="border border-hocp-border rounded-md bg-hocp-bgCard overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-hocp-border bg-hocp-bgSubtle">{["Code", "Time", "Method", "Reference", "Amount", ""].map(h => <th key={h} className={`px-3 py-2.5 text-[10px] mono uppercase tracking-wider text-hocp-textMuted ${h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
                <tbody>{payments.map(p => (
                  <tr key={p.id} className="border-b border-hocp-border last:border-0">
                    <td className="px-3 py-2.5 mono text-[12px] text-hocp-textPrimary">{p.code}</td>
                    <td className="px-3 py-2.5 mono text-[10px] text-hocp-textTertiary">{new Date(p.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2.5"><Badge tone="info">{p.method}</Badge>{p.is_refund && <Badge tone="warning">REFUND</Badge>}</td>
                    <td className="px-3 py-2.5 mono text-[11px] text-hocp-textMuted">{p.reference || "—"}</td>
                    <td className={`px-3 py-2.5 text-right mono text-[12px] ${p.is_refund ? "text-amber-400" : "text-emerald-400"}`}>{p.is_refund ? "-" : ""}{money(p.amount)}</td>
                    <td className="px-3 py-2.5 text-right">{!p.is_refund && <Button size="sm" variant="ghost" onClick={() => { setTab("documents"); setDocsKey((k) => k + 1); }} className="h-6 px-2 text-[10px]" data-testid={`receipt-for-${p.id}`}><Receipt className="w-3 h-3 mr-1"/>Receipt</Button>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          }
        </TabsContent>
        <TabsContent value="documents">
          <div className="mb-4" data-testid="stay-documents-section">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-hocp-textTertiary mb-2">Printed Documents</div>
            <StayDocumentsList stayId={id} refreshKey={docsKey} />
          </div>
          <DocumentsPanel key={docsKey} stayId={id} stay={stay} canBill={isCheckedIn} />
        </TabsContent>
        <TabsContent value="rooms">
          <div className="space-y-1.5">
            {(stay.room_history || []).map((h, i) => (
              <Card key={i} className="p-3 flex items-center gap-3">
                <div className="font-heading text-lg text-white">Room {h.room_number}</div>
                <span className="text-[10px] mono text-hocp-textMuted">{new Date(h.assigned_at).toLocaleString()}</span>
                <ArrowRight className="w-3 h-3 text-hocp-textTertiary"/>
                <span className="text-[10px] mono text-hocp-textMuted">{h.released_at ? new Date(h.released_at).toLocaleString() : "current"}</span>
                <span className="text-[11px] text-hocp-textMuted ml-auto">{h.reason}</span>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="timeline">
          <div className="space-y-1.5">
            {(stay.timeline || []).map((t, i) => (
              <div key={i} className="flex items-start gap-3 p-3 border border-hocp-border rounded-md bg-hocp-bgCard">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2"></div>
                <div className="flex-1">
                  <div className="flex justify-between"><span className="mono text-[12px] text-hocp-textPrimary">{t.event}</span><span className="mono text-[10px] text-hocp-textTertiary">{new Date(t.at).toLocaleString()}</span></div>
                  {t.note && <div className="text-[11px] text-hocp-textMuted mt-0.5">{t.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Check-in dialog */}
      <Dialog open={checkinOpen} onOpenChange={setCheckinOpen}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <DialogHeader><DialogTitle className="font-heading">Check in</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Room *</Label>
              <Select value={checkinForm.room_id} onValueChange={(v) => setCheckinForm({ ...checkinForm, room_id: v })}>
                <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="checkin-room"><SelectValue placeholder="Available room"/></SelectTrigger>
                <SelectContent className="max-h-[260px]">{rooms.filter(r => r.operational_status === "available").map(r => <SelectItem key={r.id} value={r.id}>Room {r.room_number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Note</Label><Input value={checkinForm.note} onChange={(e) => setCheckinForm({ ...checkinForm, note: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5"/></div>
          </div>
          <DialogFooter><Button onClick={doCheckin} disabled={gated} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-checkin">Check in</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post charge */}
      <Dialog open={chargeOpen} onOpenChange={setChargeOpen}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <DialogHeader><DialogTitle className="font-heading">Post charge</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Source module</Label>
              <Select value={chargeForm.source_module} onValueChange={(v) => setChargeForm({ ...chargeForm, source_module: v })}>
                <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="charge-module"><SelectValue/></SelectTrigger>
                <SelectContent>{SOURCE_MODULES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Description *</Label><Input value={chargeForm.description} onChange={(e) => setChargeForm({ ...chargeForm, description: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="charge-description"/></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Qty</Label><Input type="number" value={chargeForm.quantity} onChange={(e) => setChargeForm({ ...chargeForm, quantity: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono"/></div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Unit price *</Label><Input type="number" step="0.01" value={chargeForm.unit_price} onChange={(e) => setChargeForm({ ...chargeForm, unit_price: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="charge-unit-price"/></div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Tax %</Label><Input type="number" step="0.01" value={chargeForm.tax_rate} onChange={(e) => setChargeForm({ ...chargeForm, tax_rate: e.target.value })} placeholder="0" className="bg-hocp-bgCard border-hocp-border mt-1.5 mono"/></div>
            </div>
          </div>
          <DialogFooter><Button onClick={postCharge} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-charge">Post charge</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CollectPaymentDialog open={payOpen} onClose={() => setPayOpen(false)} stay={stay} folioId={folio?.id} summary={folioSummary} onDone={() => { load(); setDocsKey((k) => k + 1); }} />

      {/* Change room — shared modal (same component as Front Desk) */}
      <RoomChangeModal open={changeOpen} onOpenChange={setChangeOpen} stayId={stay.id} hotelId={stay.hotel_id}
                       currentRoomId={stay.current_room_id} currentRoomNumber={room?.room_number} stayCode={stay.code}
                       onSuccess={() => { toast.success("Room changed"); load(); }} />

      {/* Extend */}
      <Dialog open={extOpen} onOpenChange={setExtOpen}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <DialogHeader><DialogTitle className="font-heading">Extend stay</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">New departure *</Label><DatePicker value={(extForm.new_departure || '').slice(0,10)} onChange={(_v) => setExtForm({ ...extForm, new_departure: _v })} data-testid="extend-departure"/></div>
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Reason</Label><Input value={extForm.reason} onChange={(e) => setExtForm({ ...extForm, reason: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5"/></div>
          </div>
          <DialogFooter><Button onClick={doExt} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-extend">Extend</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <EarlyCheckinPrompt open={!!earlyPrompt} stays={earlyPrompt ? [earlyPrompt] : []} onClose={(posted) => { setEarlyPrompt(null); if (posted) load(); }} />
      {/* Checkout validation */}
      {checkoutOpen && (
        <CheckoutFlow stayId={id} hotelId={stay.hotel_id} open onCancel={() => setCheckoutOpen(false)}
          onSuccess={() => { setCheckoutOpen(false); load(); setDocsKey((k) => k + 1); }} />
      )}
    </div>
  );
}
